import { useSession } from '@context/sessionProvider';
import { createFetcher } from '@lib/hooks';
import { sendDataAPI } from '@lib/sendDataAPI';
import { toast } from 'react-toastify';
import useSWR, { useSWRConfig } from 'swr';
import { ConfigData, ConfigDataResponse } from '@/types';

type SaveSettingsResult = { isSaved: boolean; id?: number };

type SavedConfigResponse = ConfigDataResponse & { data?: ConfigData & { id?: number } };

const buildConfigKey = (tid: number) => [`/api/config/${tid}`];

export function useConfigSettings(tid?: number) {
	const { context } = useSession();
	const { mutate: mutateCache } = useSWRConfig();
	const fetcherWithToken = createFetcher(context);

	const { data, error, isLoading } = useSWR<ConfigDataResponse>(tid ? buildConfigKey(tid) : null, fetcherWithToken, {
		refreshInterval: 0,
	});
	const saveSettings = async (payload: Partial<ConfigData>, timerId?: number): Promise<SaveSettingsResult> => {
		const isEditMode = typeof timerId === 'number' && Number.isInteger(timerId);
		const endpoint = isEditMode ? `/api/config/${timerId}` : '/api/config/create';
		const method = isEditMode ? 'PUT' : 'POST';

		const response = await sendDataAPI({ jwtToken: context }, endpoint, method, JSON.stringify(payload));
		const responseData = (await response.json()) as SavedConfigResponse;

		if (!response.ok) {
			toast.error(responseData.message);

			return { isSaved: false };
		}

		toast.success(responseData.message);

		const savedConfig = responseData.data;
		const savedId = typeof savedConfig?.id === 'number' ? savedConfig.id : timerId;

		if (savedConfig && typeof savedId === 'number') {
			await mutateCache(buildConfigKey(savedId), { data: savedConfig }, false);
		}

		return { isSaved: true, id: isEditMode ? undefined : savedConfig?.id };
	};

	return {
		configData: data?.data,
		isLoading,
		error,
		saveSettings,
	};
}
